import { AppState } from 'store';
import { Dispatch } from 'redux';
import {
  fetchStatesService,
  fetchDriversService,
  searchTaxMapsService,
} from './TaxMaps.services';
import {
  GetStateSuccessType,
  SetResultLoadingType,
  SET_TAX_MAPS_LOADING,
  GET_TAX_MAPS_STATES_SUCCESS,
  GET_TAX_MAPS_DRIVERS_SUCCESS,
  TAX_MAPS_RESULT_SUCCESS,
  GetDriversSuccessType,
  TaxMapsSearchResultSuccessType,
  RESET_DRIVERS_DATA,
  ResetDriversDataType,
  RESET_RESULTS,
  ResetResultsType,
} from './TaxMaps.types';
import { httpError, json, showWarnNotification } from 'common';
import { EMPTY_VALUE } from './TaxMaps.constants';
import { TaxMapsSearchResult, USState, GetDriversResult } from './models';
import { saveFile } from 'common/helpers';
import { taxMapsExportService } from './services/taxMapsExportService';
import { getSwissCode } from './TaxMaps.selectors';

const setResultLoading = (loading: boolean): SetResultLoadingType => ({
  type: SET_TAX_MAPS_LOADING,
  loading,
});

const getStateSuccess = (usState: USState): GetStateSuccessType => ({
  type: GET_TAX_MAPS_STATES_SUCCESS,
  usState,
});

export const getState =
  (usState: string, token: string) => (dispatch: Dispatch) => {
    dispatch(setResultLoading(true));
    fetchStatesService(usState, token)
      .then(httpError(dispatch))
      .then(json)
      .then((response) => {
        if (response.errorMessage) throw Error(response.errorMessage);
        dispatch(getStateSuccess(response));
      })
      .catch((error) => {
        dispatch(setResultLoading(false));
        showWarnNotification(`States`, error.message);
      });
  };

const resultSuccess = (
  result: TaxMapsSearchResult,
): TaxMapsSearchResultSuccessType => ({
  type: TAX_MAPS_RESULT_SUCCESS,
  result,
});

export const searchTaxMaps =
  (driversData: GetDriversResult, formValues: any, token: string) =>
  (dispatch: Dispatch, getState: () => AppState) => {
    const {
      driversSearch,
      // @ts-ignore:
      hobCodes: { countyHob },
    } = formValues;
    // @ts-ignore:
    const counties = getState().taxMaps.usState.counties;
    const swissCode = getSwissCode(formValues.hobCodes, counties);
    const body = {
      ...driversData,
      hob: countyHob,
      drivers: driversData.drivers.map((driver) => ({
        ...driver,
        value:
          driversSearch[driver.fIndex] ||
          (driver.name === 'Swis' && swissCode
            ? swissCode.substring(2)
            : EMPTY_VALUE),
      })),
    };
    dispatch(setResultLoading(true));
    dispatch(resetResults());
    searchTaxMapsService(body, token)
      .then(httpError(dispatch))
      .then(json)
      .then((response: any) => {
        if (response.errorMessage) throw Error(response.errorMessage);
        dispatch(resultSuccess(response));
      })
      .catch((error: any) => {
        dispatch(setResultLoading(false));
        showWarnNotification('Tax Maps', error.message);
      });
  };

const getDriversSuccess = (
  driversData: GetDriversResult,
): GetDriversSuccessType => ({
  type: GET_TAX_MAPS_DRIVERS_SUCCESS,
  driversData,
});

export const getDrivers =
  (hobCode: string, driversData: GetDriversResult, token: string) =>
  (dispatch: Dispatch) => {
    dispatch(setResultLoading(true));
    fetchDriversService(hobCode, token)
      .then(httpError(dispatch))
      .then(json)
      .then((response) => {
        if (response.errorMessage) throw Error(response.errorMessage);
        const result = response.drivers.length
          ? response
          : { ...driversData, hob: response.hob };
        dispatch(getDriversSuccess(result));
      })
      .catch((error) => {
        dispatch(setResultLoading(false));
        showWarnNotification('Tax Maps Drivers', error.message);
      });
  };

export const resetDriversData = (): ResetDriversDataType => ({
  type: RESET_DRIVERS_DATA,
});

export const resetResults = (): ResetResultsType => ({
  type: RESET_RESULTS,
});

export const exportReport =
  (
    searchIdId: number,
    middlename: string,
    imageId: number,
    imageName: string,
    format: string,
    token: string,
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(setResultLoading(true));
    try {
      const blob = await taxMapsExportService(
        searchIdId,
        middlename,
        imageId,
        token,
        format,
        dispatch,
      );
      dispatch(setResultLoading(false));
      return saveFile(blob, `${imageName}.${format}`);
    } catch (error) {
      const errorObject = error as any;
      dispatch(setResultLoading(false));
      showWarnNotification('Tax Maps Export', errorObject?.message);
    }
  };
